import React, { MouseEvent } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import { Avatar, AvatarSize, AvatarProps, AvatarVariant } from '@televet/kibble-ui';
import { ClinicUserAvatarFragment } from 'shared/types/graphql';

interface DefaultClinicAvatarProps extends AvatarProps {
  size?: AvatarSize;
  showTooltip?: boolean;
  showRemoveButton?: boolean;
  onClick?: (e: MouseEvent) => void;
  onRemove?: (e: MouseEvent) => void;
  isGrouped?: boolean;
  index?: number;
  variant?: AvatarVariant;
}

interface ClinicSystemAvatarProps extends DefaultClinicAvatarProps {
  isAutomated?: boolean;
  isClinic?: boolean;
  clinicMember?: never;
}

interface ClinicMemberAvatarProps extends DefaultClinicAvatarProps {
  clinicMember: ClinicUserAvatarFragment;
  isAutomated?: never;
  isClinic?: never;
}

export type ClinicAvatarProps = ClinicMemberAvatarProps | ClinicSystemAvatarProps;

const ClinicAvatar = ({
  clinicMember,
  size = 'md',
  isAutomated,
  isClinic,
  showTooltip = true,
  showRemoveButton = false,
  isGrouped = false,
  index,
  onRemove,
  onClick,
  variant = 'outlineAccent',
  ...rest
}: ClinicAvatarProps): JSX.Element => {
  const { firstName, lastName, displayName, profilePic } = clinicMember || {};
  const fullName = [firstName, lastName].filter((n) => !!n).join(' ');
  const { currentClinic } = useClinicUser();

  return (
    <Avatar
      name={fullName}
      showRemoveButton={!!showRemoveButton}
      showTooltip={showTooltip}
      tooltipText={displayName || fullName}
      size={size}
      data-testid="clinic-avatar"
      handleRemoveButtonClick={onRemove}
      variant={variant}
      cursor="pointer"
      {...(isGrouped && { marginInlineStart: index === 0 ? 0 : '-2' })}
      {...(profilePic && { src: profilePic })}
      {...(onClick && { handleClick: onClick })}
      {...(isClinic && {
        tooltipText: currentClinic?.name,
        name: currentClinic?.name,
        ...(currentClinic?.logo && { src: currentClinic.logo }),
        ...(!currentClinic?.name && { getInitials: (): string => 'Sys' }),
      })}
      {...(isAutomated && {
        iconName: 'robot',
        backgroundColor: 'background.primary',
        hideBorder: true,
        color: 'text.onContrast',
        size: 'sm',
      })}
      {...rest}
    />
  );
};

export default ClinicAvatar;
